"use client";

import { logoutWithConfirm } from "@/lib/logout";
import Link from "next/link";
import { usePathname } from "next/navigation";
import {
    Home,
    Truck,
    ChevronLeft,
    Menu,
    ChevronDown,
    ChevronUp,
    LogOut,
    Search,
} from "lucide-react";
import { useState, useEffect, useCallback, useRef, useLayoutEffect, useMemo } from "react";
import { useCanSeeMenu } from "@/hooks/useCanSeeMenu";
import useSNConfig from "@/hooks/useSNConfig";
// Adopsi Fase 2 sidebar MGMX (2026-09-23): data menu dipindah ke `src/lib/menuTree.ts`
// supaya bisa dipakai helper lain (mis. `buildBreadcrumbForMenu()` di Fase 3) tanpa
// membuat import siklik ke komponen React ini.
import {
    menuGroups,
    GROUP_COLORS as groupColors,
    hasActiveInTree,
    namaTampil,
    type SubMenuItem,
} from "@/lib/menuTree";

interface SidebarProps {
    isOpen: boolean;
    toggleSidebar: () => void;
}


const singleMenuItems = [
    { name: "Dashboard", href: "/dashboard", icon: Home }
];

export default function Sidebar({ isOpen, toggleSidebar }: SidebarProps) {
    const pathname = usePathname();
    const [searchQuery, setSearchQuery] = useState("");
    const [expandedGroups, setExpandedGroups] = useState<string[]>([]);
    const [isInitialized, setIsInitialized] = useState(false);
    const sidebarRef = useRef<HTMLElement>(null);

    // Aturan visibilitas menu (paket + IsShow DB + role user) — dipakai bersama Breadcrumb: lihat hooks/useCanSeeMenu.ts
    const canSee = useCanSeeMenu();
    // Muat label SN (judul menu Serial Number ikut label) & render ulang saat label berubah
    const { label: snLabel, labelShort: snLabelShort } = useSNConfig();

    const hasVisibleItems = useCallback((items: SubMenuItem[]): boolean => {
        const walk = (list: SubMenuItem[]): boolean =>
            list.some(item => (item.items ? walk(item.items) : canSee(item.kdMenu)));
        return walk(items);
    }, [canSee]);

    const visibleGroups = useMemo(() => {
        const query = searchQuery.toLowerCase();

        return menuGroups.map(group => {
            // First, filter by permissions
            const permittedSubGroups = group.items.map(subGroup => {
                const permittedLeafs = subGroup.items?.filter(item => canSee(item.kdMenu)) || [];
                return { ...subGroup, items: permittedLeafs };
            }).filter(subGroup => subGroup.items && subGroup.items.length > 0);

            if (!query) {
                return { ...group, items: permittedSubGroups };
            }

            // Then, filter by search query
            const groupMatches = group.name.toLowerCase().includes(query);
            
            const searchedSubGroups = permittedSubGroups.map(subGroup => {
                const subMatches = subGroup.name.toLowerCase().includes(query) || groupMatches;
                
                const searchedLeafs = subGroup.items?.filter(item => 
                    subMatches || namaTampil(item).toLowerCase().includes(query)
                ) || [];

                return { ...subGroup, items: searchedLeafs };
            }).filter(subGroup => subGroup.items && subGroup.items.length > 0);

            return { ...group, items: searchedSubGroups };
        }).filter(group => group.items.length > 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- snLabel/snLabelShort: judul menu SN (label) diisi di luar React, hasil pencarian ikut berubah
    }, [searchQuery, canSee, snLabel, snLabelShort]);

    // Initialize from localStorage (Stable: Runs once)
    useEffect(() => {
        const saved = localStorage.getItem("sidebarExpandedGroups");
        if (saved) {
            try {
                // Sinkronisasi dari localStorage (store eksternal, hanya ada di client) — tak bisa jadi initial state (hydration).
                // eslint-disable-next-line react-hooks/set-state-in-effect
                setExpandedGroups(JSON.parse(saved));
            } catch (e) {
                console.error("Failed to parse sidebar expanded groups", e);
            }
        } else {
            setExpandedGroups([]);
        }
        setIsInitialized(true);
    }, []);

    // Persist to localStorage (Stable: Depends on expandedGroups)
    useEffect(() => {
        if (isInitialized) {
            localStorage.setItem("sidebarExpandedGroups", JSON.stringify(expandedGroups));
        }
    }, [expandedGroups, isInitialized]);

    // Keep track of scroll position to restore after navigation state updates
    const savedScrollRef = useRef<number | null>(null);

    // Search-based auto-expansion
    useEffect(() => {
        if (searchQuery) {
            // Turunan dari pencarian + hasil filter izin (visibleGroups) — ikut berubah saat izin selesai dimuat.
            // eslint-disable-next-line react-hooks/set-state-in-effect
            setExpandedGroups(prev => {
                const toExpand = new Set(prev);
                visibleGroups.forEach(g => {
                    toExpand.add(g.name);
                    g.items.forEach(sub => {
                        toExpand.add(sub.name);
                    });
                });
                return Array.from(toExpand);
            });
        }
    }, [searchQuery, visibleGroups]);

    // Navigation-based auto-expansion (Stable: Depends ONLY on pathname)
    // This allows users to manually collapse submenus without the effect forcing them open again
    useEffect(() => {
        // Save scroll position before the state change causes a re-render
        if (sidebarRef.current) {
            savedScrollRef.current = sidebarRef.current.scrollTop;
        }
        // Urutan wajib: simpan scroll (di atas) SEBELUM state berubah → dipulihkan useLayoutEffect setelah render.
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setExpandedGroups(prev => {
            const newExpanded = [...prev];
            let changed = false;

            const checkAndExpand = (items: SubMenuItem[]) => {
                let foundInBranch = false;
                items.forEach(item => {
                    const isActive = item.href && (pathname === item.href || pathname.startsWith(item.href + "/"));
                    const hasNestedActive = item.items && checkAndExpand(item.items);

                    if (isActive || hasNestedActive) {
                        foundInBranch = true;
                        if (item.items && !newExpanded.includes(item.name)) {
                            newExpanded.push(item.name);
                            changed = true;
                        }
                    }
                });
                return foundInBranch;
            };

            visibleGroups.forEach(group => {
                const hasActive = checkAndExpand(group.items);
                if (hasActive && !newExpanded.includes(group.name)) {
                    newExpanded.push(group.name);
                    changed = true;
                }
            });

            return changed ? newExpanded : prev;
        });
    }, [pathname, visibleGroups]);

    // Persistence: Synchronously restore scroll position from sessionStorage or Ref
    useLayoutEffect(() => {
        const saved = sessionStorage.getItem("sidebar_scroll_pos");
        if (saved && sidebarRef.current) {
            sidebarRef.current.scrollTop = parseInt(saved, 10);
        } else if (savedScrollRef.current !== null && sidebarRef.current) {
            sidebarRef.current.scrollTop = savedScrollRef.current;
        }
        savedScrollRef.current = null;
    });

    const saveScroll = useCallback(() => {
        if (sidebarRef.current) {
            const pos = sidebarRef.current.scrollTop;
            savedScrollRef.current = pos;
            sessionStorage.setItem("sidebar_scroll_pos", pos.toString());
        }
    }, []);

    const toggleGroup = useCallback((groupName: string) => {
        saveScroll();
        setExpandedGroups(prev =>
            prev.includes(groupName)
                ? prev.filter(g => g !== groupName)
                : [...prev, groupName]
        );
    }, [saveScroll]);

    const handleLogout = logoutWithConfirm;

    const renderMenuItems = (items: SubMenuItem[], depth = 0) => {
        // Filter items based on permissions
        const visibleItems = items.filter(item => {
            if (item.items) return hasVisibleItems(item.items);
            return canSee(item.kdMenu);
        });

        return visibleItems.map((item) => {
            const isGroup = !!item.items;
            const isExpanded = expandedGroups.includes(item.name);
            const isActive = item.href ? (pathname === item.href) : false;
            const hasChildActive = item.items ? hasActiveInTree(item.items, pathname) : false;
            const Icon = item.icon;

            if (isGroup) {
                return (
                    <div key={item.name} className="space-y-0.5">
                        <button
                            onClick={() => toggleGroup(item.name)}
                            className={`w-full flex items-center px-3 py-2 text-xs font-semibold rounded-lg transition-all duration-150 ${(isActive || hasChildActive)
                                ? "bg-white/10 text-slate-100"
                                : "text-slate-400 hover:bg-white/6 hover:text-slate-200"
                                }`}
                        >
                            <Icon className={`w-3.5 h-3.5 mr-2 shrink-0 ${(isActive || hasChildActive) ? "opacity-80" : "opacity-40"}`} />
                            <span className="flex-1 text-left">{item.name}</span>
                            {isExpanded ? (
                                <ChevronUp size={12} className="opacity-40 shrink-0" />
                            ) : (
                                <ChevronDown size={12} className="opacity-40 shrink-0" />
                            )}
                        </button>
                        {isExpanded && (
                            <div className="ml-3.5 space-y-0.5 border-l border-slate-700/50 pl-2">
                                {renderMenuItems(item.items!, depth + 1)}
                            </div>
                        )}
                    </div>
                );
            }

            return (
                <Link
                    key={item.href}
                    href={item.href!}
                    onClick={saveScroll}
                    className={`flex items-center px-3 py-1.5 text-xs rounded-lg transition-all duration-150 ${isActive
                        ? "bg-sky-500/25 text-sky-200 font-semibold"
                        : "text-slate-400 hover:bg-white/6 hover:text-slate-200"
                        }`}
                >
                    <span className={`w-1 h-1 rounded-full mr-2.5 shrink-0 ${isActive ? "bg-sky-400" : "bg-slate-600"}`}></span>
                    <span>{namaTampil(item)}</span>
                </Link>
            );
        });
    };

    // Filter groups that have at least one visible item (moved to top of component)

    return (
        <>
            {/* Mobile Sidebar Overlay */}
            {isOpen && (
                <div
                    className="fixed inset-0 bg-black/50 z-30 md:hidden"
                    onClick={toggleSidebar}
                />
            )}

            <aside
                ref={sidebarRef}
                className={`bg-slate-950 text-white h-screen flex flex-col fixed md:relative left-0 top-0 overflow-y-auto scrollbar-hide z-40 transition-all duration-300 ease-in-out print:hidden ${isOpen ? "w-64 translate-x-0" : "-translate-x-full w-0"
                    }`}
            >
                {/* Header / Brand */}
                <div className={`sticky top-0 z-10 bg-slate-950 border-b border-white/8 flex flex-col shrink-0 ${isOpen ? "p-3" : "px-3 py-3.5"} bg-gradient-to-r from-slate-800/60 to-slate-900/40`}>
                    <div className={`flex items-center ${isOpen ? "justify-between" : "justify-center"}`}>
                        {isOpen && (
                            <div className="flex items-center gap-3 min-w-0">
                                <div className="w-8 h-8 bg-gradient-to-br from-sky-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg shadow-sky-500/30 shrink-0">
                                    <Truck className="w-4 h-4 text-white" />
                                </div>
                                <div className="min-w-0">
                                    <h1 className="text-sm font-bold text-white leading-tight tracking-wide">MG Trucking</h1>
                                    <p className="text-[10px] text-sky-400/80 font-medium tracking-wider">SISTEM INFORMASI</p>
                                </div>
                            </div>
                        )}
                        <button
                            onClick={toggleSidebar}
                            className="md:hidden p-1.5 rounded-lg hover:bg-white/10 text-slate-500 hover:text-white transition-colors shrink-0"
                        >
                            {isOpen ? <ChevronLeft size={18} /> : <Menu size={18} />}
                        </button>
                    </div>

                    {isOpen && (
                        <div className="mt-3 relative">
                            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                            <input 
                                type="text"
                                placeholder="Cari menu..."
                                value={searchQuery}
                                onChange={e => setSearchQuery(e.target.value)}
                                className="w-full bg-slate-900/50 border border-slate-700/50 text-slate-300 text-xs rounded-lg pl-8 pr-3 py-2 focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500/50 transition-all placeholder:text-slate-600"
                            />
                        </div>
                    )}
                </div>

                <nav className="flex-1 px-2.5 py-3 space-y-1 mt-1">
                    {singleMenuItems.map((item) => {
                        const isActive = pathname === item.href;
                        const Icon = item.icon;

                        return (
                            <Link
                                key={item.href}
                                href={item.href}
                                className={`flex items-center px-3 py-2.5 text-sm font-semibold rounded-xl transition-all duration-150 group relative ${isActive
                                    ? "bg-sky-500/20 text-sky-300 ring-1 ring-sky-500/30"
                                    : "text-slate-400 hover:bg-white/6 hover:text-slate-200"
                                    } ${!isOpen && "justify-center"}`}
                                title={!isOpen ? item.name : ""}
                            >
                                <Icon className={`w-4.5 h-4.5 ${isOpen ? "mr-3" : ""} ${isActive ? "text-sky-400" : "text-slate-500"}`} />
                                {isOpen && <span className="text-[13px]">{item.name}</span>}

                                {!isOpen && (
                                    <div className="absolute left-full ml-4 px-2 py-1 bg-slate-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 whitespace-nowrap z-50 pointer-events-none transition-opacity">
                                        {item.name}
                                    </div>
                                )}
                            </Link>
                        );
                    })}

                    {visibleGroups.map((group) => {
                        const isExpanded = expandedGroups.includes(group.name);
                        const GroupIcon = group.icon;
                        const hasActiveItem = hasActiveInTree(group.items, pathname);
                        const colors = groupColors[group.name] ?? { dot: "bg-slate-500", activeBg: "bg-white/8", activeText: "text-slate-200" };

                        return (
                            <div key={group.name} className="space-y-0.5">
                                <button
                                    onClick={() => isOpen && toggleGroup(group.name)}
                                    className={`w-full flex items-center px-2.5 py-2.5 text-[13px] font-semibold rounded-xl transition-all duration-150 group relative ${hasActiveItem
                                        ? `${colors.activeBg} ${colors.activeText} ring-1 ring-white/10`
                                        : "text-slate-400 hover:bg-white/6 hover:text-slate-200"
                                        } ${!isOpen && "justify-center"}`}
                                    title={!isOpen ? group.name : ""}
                                >
                                    {isOpen && <span className={`w-1.5 h-1.5 rounded-full mr-2 shrink-0 ${colors.dot} ${hasActiveItem ? "opacity-100" : "opacity-50"}`}></span>}
                                    <GroupIcon className={`w-4 h-4 ${isOpen ? "mr-2" : ""} ${hasActiveItem ? "opacity-90" : "opacity-50"}`} />
                                    {isOpen && (
                                        <>
                                            <span className="flex-1 text-left">{group.name}</span>
                                            {isExpanded ? (
                                                <ChevronUp size={13} className="opacity-40" />
                                            ) : (
                                                <ChevronDown size={13} className="opacity-40" />
                                            )}
                                        </>
                                    )}

                                    {!isOpen && (
                                        <div className="absolute left-full ml-4 px-2 py-1 bg-slate-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 whitespace-nowrap z-50 pointer-events-none transition-opacity">
                                            {group.name}
                                        </div>
                                    )}
                                </button>

                                {isOpen && isExpanded && (
                                    <div className="ml-4 space-y-0.5 border-l border-slate-700/60 pl-2 mt-0.5">
                                        {renderMenuItems(group.items)}
                                    </div>
                                )}

                                {!isOpen && (
                                    <div className="hidden group-hover:block absolute left-full ml-2 bg-slate-800 rounded-lg shadow-lg p-2 min-w-[200px] z-50">
                                        <div className="text-xs font-semibold text-slate-300 mb-2 px-2">{group.name}</div>
                                        {group.items
                                            .filter(item => {
                                                if (item.items) return hasVisibleItems(item.items);
                                                return canSee(item.kdMenu);
                                            })
                                            .map((item) => {
                                                const isGroup = !!item.items;
                                                const isActive = item.href ? pathname === item.href : item.items?.some(sub => pathname === sub.href);
                                                const Icon = item.icon;

                                                if (isGroup) {
                                                    return (
                                                        <div key={item.name} className="py-1">
                                                            <div className="flex items-center px-2 py-1 text-[10px] uppercase tracking-wider text-slate-500 font-bold">
                                                                {item.name}
                                                            </div>
                                                            {item.items
                                                                ?.filter(sub => canSee(sub.kdMenu))
                                                                .map(sub => (
                                                                    <Link
                                                                        key={sub.href}
                                                                        href={sub.href || "#"}
                                                                        onClick={saveScroll}
                                                                        className={`flex items-center px-2 py-1.5 text-xs rounded transition-colors ${pathname === sub.href
                                                                            ? "bg-sky-600 text-white"
                                                                            : "text-slate-400 hover:bg-slate-700 hover:text-white"
                                                                            }`}
                                                                    >
                                                                        <sub.icon className="w-3 h-3 mr-2" />
                                                                        <span>{namaTampil(sub)}</span>
                                                                    </Link>
                                                                ))}
                                                        </div>
                                                    );
                                                }

                                                return (
                                                    <Link
                                                        key={item.href}
                                                        href={item.href || "#"}
                                                        className={`flex items-center px-2 py-2 text-xs rounded transition-colors ${isActive
                                                            ? "bg-sky-600 text-white"
                                                            : "text-slate-400 hover:bg-slate-700 hover:text-white"
                                                            }`}
                                                    >
                                                        <Icon className="w-3 h-3 mr-2" />
                                                        <span>{namaTampil(item)}</span>
                                                    </Link>
                                                );
                                            })}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </nav>

                <div className="p-2.5 border-t border-white/8">
                    <button
                        onClick={handleLogout}
                        className={`flex items-center w-full px-3 py-2.5 text-sm font-semibold rounded-xl transition-all duration-150 text-slate-500 hover:bg-red-500/10 hover:text-red-400 group relative ${!isOpen && "justify-center"}`}
                        title={!isOpen ? "Logout" : ""}
                    >
                        <LogOut className={`w-4 h-4 ${isOpen ? "mr-3" : ""}`} />
                        {isOpen && <span className="text-[13px]">Keluar</span>}

                        {!isOpen && (
                            <div className="absolute left-full ml-4 px-2 py-1 bg-slate-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 whitespace-nowrap z-50 pointer-events-none transition-opacity">
                                Logout
                            </div>
                        )}
                    </button>
                </div>

                <div className="px-4 py-2.5 border-t border-white/5">
                    <div className="text-[10px] text-slate-600 text-center font-medium tracking-wider">
                        {isOpen ? "© 2026 MG SOLUTION" : "©"}
                    </div>
                </div>
            </aside>
        </>
    );
}
